import React, { useState, useEffect, useCallback } from 'react';
import { Star, Trophy, Award, Camera, PartyPopper, Heart, TrendingUp, Crown, Sparkles, Volume2 } from 'lucide-react';

// Sound URLs (free notification sounds from mixkit)
const SOUNDS = {
  points: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3', // Positive notification chime
  badge: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3',  // Achievement unlock fanfare
  rankUp: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3'  // Level up sound
};

// Audio state management
let audioEnabled = false;
let audioContext = null;

// Initialize audio on user interaction (required by browsers)
export const initAudio = () => {
  if (audioEnabled) return;
  
  try {
    // Create AudioContext to unlock audio
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    // Play a silent sound to unlock audio
    const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
    silentAudio.volume = 0.01;
    silentAudio.play().then(() => {
      audioEnabled = true;
      // console.log('🔊 Audio enabled successfully');
    }).catch(e => {
      // console.log('Audio init warning:', e);
      audioEnabled = true; // Try anyway
    });
  } catch (e) {
    // console.log('AudioContext error:', e);
    audioEnabled = true; // Try anyway
  }
};

// Play sound with fresh Audio element each time
const playSound = (soundKey) => {
  if (!SOUNDS[soundKey]) return;
  
  try {
    // Always create a new Audio element for reliability
    const audio = new Audio(SOUNDS[soundKey]);
    audio.volume = 0.6;
    
    // Add event listeners for debugging
    audio.addEventListener('canplaythrough', () => {
      audio.play().then(() => {
        // console.log(`🎵 Playing: ${soundKey}`);
      }).catch(e => {
        // console.log(`Sound blocked (${soundKey}):`, e.message);
      });
    });
    
    audio.addEventListener('error', (e) => {
      // console.log(`Audio load error (${soundKey}):`, e);
    });
    
    // Start loading
    audio.load();
  } catch (e) {
    // console.log('Sound error:', e);
  }
};

export const playPointsSound = () => playSound('points');
export const playBadgeSound = () => playSound('badge');
export const playRankUpSound = () => playSound('rankUp');

// Sound toggle indicator component
export const SoundIndicator = ({ enabled = true }) => (
  <div className="fixed bottom-4 right-4 bg-white/90 px-3 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm z-50">
    <Volume2 className={`w-4 h-4 ${enabled ? 'text-green-500' : 'text-gray-400'}`} />
    <span className={enabled ? 'text-green-600' : 'text-gray-500'}>
      {enabled ? 'Sound On' : 'Sound Off'}
    </span>
  </div>
);

// Points Pop-up Animation Component
export const PointsPopup = ({ points, type, onComplete }) => {
  const [visible, setVisible] = useState(true);
  
  useEffect(() => {
    playPointsSound();
    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!visible) return null;

  const getIcon = () => {
    switch (type) {
      case 'photo': return <Camera className="w-8 h-8" />;
      case 'special_occasion': return <PartyPopper className="w-8 h-8" />;
      case 'social_media': return <Star className="w-8 h-8" />;
      case 'positive_feedback': return <Heart className="w-8 h-8" />;
      default: return <Heart className="w-8 h-8" />;
    }
  };

  const getColor = () => {
    switch (type) {
      case 'photo': return 'from-blue-500 to-cyan-500';
      case 'special_occasion': return 'from-purple-500 to-pink-500';
      case 'social_media': return 'from-pink-500 to-rose-500';
      case 'positive_feedback': return 'from-green-500 to-emerald-500';
      default: return 'from-red-500 to-pink-500';
    }
  };

  return (
    <div className="fixed top-20 right-8 z-[100] animate-bounce-in" data-testid="points-popup">
      <div className={`bg-gradient-to-r ${getColor()} text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 transform`}>
        <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
          {getIcon()}
        </div>
        <div>
          <div className="text-3xl font-bold flex items-center gap-2">
            +{points} <span className="text-2xl">❤️</span>
          </div>
          <div className="text-sm text-white/80">Hearts Earned!</div>
        </div>
        <div className="absolute -top-2 -right-2">
          <Sparkles className="w-6 h-6 text-yellow-300 animate-spin" />
        </div>
      </div>
    </div>
  );
};

// Badge Unlock Animation Component
export const BadgeUnlockAnimation = ({ badge, onComplete }) => {
  const [phase, setPhase] = useState('intro'); // intro, reveal, celebrate
  
  useEffect(() => {
    playBadgeSound();
    
    const timers = [
      setTimeout(() => setPhase('reveal'), 500),
      setTimeout(() => setPhase('celebrate'), 1500),
      setTimeout(() => onComplete?.(), 4000)
    ];
    
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  const getBadgeInfo = () => {
    switch (badge) {
      case 'paparazzi':
        return { icon: '📸', name: 'Paparazzi', color: 'from-cyan-500 to-blue-600', points: 20 };
      case 'master_butler':
        return { icon: '🎩', name: 'Master Butler', color: 'from-amber-500 to-orange-600', points: 20 };
      case 'monthly_first':
        return { icon: '👑', name: 'Monthly Champion', color: 'from-yellow-400 to-amber-500', points: 50 };
      case 'monthly_second':
        return { icon: '🥈', name: 'Runner Up', color: 'from-gray-300 to-gray-500', points: 30 };
      case 'monthly_third':
        return { icon: '🥉', name: 'Third Place', color: 'from-orange-400 to-orange-600', points: 20 };
      default:
        return { icon: '🏆', name: 'Achievement', color: 'from-purple-500 to-indigo-600', points: 10 };
    }
  };

  const info = getBadgeInfo();

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85" data-testid="badge-animation">
      {/* Confetti-like particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 rounded-full animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-10px',
              backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][i % 6],
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className={`text-center transform transition-all duration-500 ${
        phase === 'intro' ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
      }`}>
        {/* Badge Icon */}
        <div className={`relative mx-auto mb-6 ${phase === 'celebrate' ? 'animate-bounce' : ''}`}>
          <div className={`w-40 h-40 bg-gradient-to-br ${info.color} rounded-full flex items-center justify-center shadow-2xl border-4 border-white/30`}>
            <span className="text-8xl">{info.icon}</span>
          </div>
          {phase === 'celebrate' && (
            <>
              <div className="absolute -top-4 -right-4 animate-spin">
                <Sparkles className="w-10 h-10 text-yellow-400" />
              </div>
              <div className="absolute -bottom-4 -left-4 animate-spin" style={{ animationDirection: 'reverse' }}>
                <Sparkles className="w-10 h-10 text-yellow-400" />
              </div>
            </>
          )}
        </div>

        {/* Badge Name */}
        <h2 className={`text-4xl font-bold text-white mb-2 transition-all duration-300 ${
          phase === 'celebrate' ? 'animate-pulse' : ''
        }`}>
          🎉 Badge Unlocked! 🎉
        </h2>
        <h3 className={`text-3xl font-bold bg-gradient-to-r ${info.color} bg-clip-text text-transparent mb-4`}>
          {info.name}
        </h3>
        
        {/* Bonus Points */}
        {phase === 'celebrate' && (
          <div className="animate-bounce-in">
            <div className="inline-flex items-center gap-3 bg-white/10 px-6 py-3 rounded-full">
              <span className="text-2xl text-white font-bold">+{info.points}</span>
              <span className="text-3xl">❤️</span>
              <span className="text-white/80">Bonus Hearts!</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Ranking Climb Animation
export const RankingClimbAnimation = ({ oldRank, newRank, onComplete }) => {
  const [visible, setVisible] = useState(true);
  
  useEffect(() => {
    playRankUpSound();
    const timer = setTimeout(() => {
      setVisible(false);
      onComplete?.();
    }, 3500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!visible) return null;

  const getRankIcon = (rank) => {
    if (rank === 1) return '👑';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70" data-testid="ranking-animation">
      <div className="text-center animate-bounce-in">
        <div className="text-6xl mb-4">🚀</div>
        <h2 className="text-3xl font-bold text-white mb-6">You're Moving Up!</h2>
        
        <div className="flex items-center justify-center gap-6">
          {/* Old Rank */}
          <div className="text-center opacity-50">
            <div className="w-20 h-20 bg-gray-600 rounded-full flex items-center justify-center text-3xl mb-2">
              {getRankIcon(oldRank)}
            </div>
            <span className="text-gray-400">Was #{oldRank}</span>
          </div>
          
          {/* Arrow */}
          <TrendingUp className="w-12 h-12 text-green-400 animate-bounce" />
          
          {/* New Rank */}
          <div className="text-center animate-pulse">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center text-4xl mb-2 shadow-lg ${
              newRank === 1 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
              newRank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
              newRank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-500' :
              'bg-gradient-to-br from-primary to-cyan-500'
            }`}>
              {getRankIcon(newRank)}
            </div>
            <span className="text-white font-bold text-xl">Now #{newRank}!</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Points Counter Animation
export const PointsCounter = ({ targetPoints, duration = 2000 }) => {
  const [displayPoints, setDisplayPoints] = useState(0);
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayPoints(Math.floor(easeOutQuart * targetPoints));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [targetPoints, duration]);

  return (
    <span className="tabular-nums">{displayPoints}</span>
  );
};

// Animated Icon Wrapper
export const AnimatedIcon = ({ children, animation = 'pulse', className = '' }) => {
  const animationClasses = {
    pulse: 'animate-pulse',
    bounce: 'animate-bounce',
    spin: 'animate-spin',
    ping: 'animate-ping',
    wiggle: 'animate-wiggle',
    float: 'animate-float'
  };

  return (
    <span className={`inline-block ${animationClasses[animation] || ''} ${className}`}>
      {children}
    </span>
  );
};

// Animation Manager Hook
export const useAnimationQueue = () => {
  const [queue, setQueue] = useState([]);
  const [currentAnimation, setCurrentAnimation] = useState(null);

  const addAnimation = useCallback((animation) => {
    setQueue(prev => [...prev, animation]);
  }, []);

  useEffect(() => {
    if (!currentAnimation && queue.length > 0) {
      setCurrentAnimation(queue[0]);
      setQueue(prev => prev.slice(1));
    }
  }, [queue, currentAnimation]);

  const onAnimationComplete = useCallback(() => {
    setCurrentAnimation(null);
  }, []);

  return { currentAnimation, addAnimation, onAnimationComplete };
};

export default {
  PointsPopup,
  BadgeUnlockAnimation,
  RankingClimbAnimation,
  PointsCounter,
  AnimatedIcon,
  useAnimationQueue,
  playPointsSound,
  playBadgeSound,
  playRankUpSound,
  initAudio
};
